import config from 'js/app/config';

import type { EntWebsiteDomain } from 'bundles/ent-website/components/EntWebsiteDomainContext';
import { PRIORITY_CTAS } from 'bundles/ent-website/components/PriorityCTA';
import { PROMOTIONS } from 'bundles/ent-website/components/Promotion';
import type { LinkObject } from 'bundles/ent-website/contentful/core/Navigation';

import _t from 'i18n!nls/ent-website';

export const DOMAINS = {
  root: 'root',
  dev: 'dev',
  c4b: 'c4b',
  c4g: 'c4g',
  c4c: 'c4c',
  c4t: 'c4t',
  enterprise: 'enterprise',
  partners: 'partners',
  skillsReports: 'skillsReports',
  impact: 'impact',
} as const;

export const DOMAIN_URL_MAPPINGS = {
  [DOMAINS.root]: '',
  [DOMAINS.skillsReports]: '',
  [DOMAINS.dev]: 'ent-website',
  [DOMAINS.c4b]: 'business',
  [DOMAINS.c4g]: 'government',
  [DOMAINS.c4c]: 'campus',
  [DOMAINS.c4t]: 'business/teams',
  [DOMAINS.enterprise]: 'enterprise/resources',
  [DOMAINS.partners]: 'partnerships',
  [DOMAINS.impact]: 'social-impact',
} as const;

export const DOMAIN_NAME_MAPPINGS: Record<EntWebsiteDomain, string> = {
  [DOMAINS.root]: _t('Coursera for Business'),
  [DOMAINS.skillsReports]: _t('Skill Reports'),
  [DOMAINS.dev]: _t('Dev'),
  [DOMAINS.c4b]: _t('Business'),
  [DOMAINS.c4g]: _t('Government'),
  [DOMAINS.c4c]: _t('Campus'),
  [DOMAINS.c4t]: _t('Teams'),
  [DOMAINS.enterprise]: _t('Enterprise'),
  [DOMAINS.partners]: _t('Partners'),
  [DOMAINS.impact]: _t('Social Impact'),
} as const;

export const priorityCtaOverride = {
  [DOMAINS.c4b]: {
    href: `${config.url.base}business/learn-more`,
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.c4t]: {
    href: '',
    priorityCtaOverride: PRIORITY_CTAS.C4T,
  },
  [DOMAINS.c4g]: {
    href: `${config.url.base}government/learn-more`,
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.c4c]: {
    href: `${config.url.base}campus/learn-more`,
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.enterprise]: {
    href: '',
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.partners]: {
    href: `${config.url.base}partnerships/learn-more`,
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.dev]: {
    href: `${config.url.base}business/learn-more`,
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.root]: {
    href: '',
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.skillsReports]: {
    href: '',
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
  [DOMAINS.impact]: {
    href: '',
    priorityCtaOverride: PRIORITY_CTAS.DEFAULT,
  },
};

const c4bLinks: LinkObject[] = [
  {
    text: 'Why Coursera',
    href: undefined,
    children: [
      {
        heading: 'Why Coursera',
        description: undefined,
        items: [
          {
            ctaText: 'Overview',
            ctaDescription: 'Upskill your workforce with an enterprise-grade skills platform',
            ctaTarget: `${config.url.base}business/why-coursera`,
          },
          {
            ctaText: 'World-Class Content',
            ctaDescription: 'Offer learners content from Yale, Duke, Google and more',
            ctaTarget: `${config.url.base}business/content`,
          },
          {
            ctaText: 'SkillSets',
            ctaDescription: 'Drive transformation with job-based learning for digital roles',
            ctaTarget: `${config.url.base}business/products/skillsets`,
          },
          {
            ctaText: 'Guided Projects',
            ctaDescription: 'Increase skills proficiency and durability with applied learning',
            ctaTarget: `${config.url.base}business/guided-projects`,
          },
          {
            ctaText: 'Skills Dashboard',
            ctaDescription: 'Track, measure, and benchmark skills to maximize training effectiveness',
            ctaTarget: `${config.url.base}business/products/skillsdashboard`,
          },
          {
            ctaText: 'Clips',
            ctaDescription: 'Drive continuous learning with 5-10 minute videos',
            ctaTarget: `${config.url.base}business/clips`,
          },
          {
            ctaText: 'Integrations',
            ctaDescription: 'Connect Coursera to your learning platforms',
            ctaTarget: `${config.url.base}business/integrations`,
          },
          {
            ctaText: 'LevelSets',
            ctaDescription: 'Provide targeted upskilling',
            ctaTarget: `${config.url.base}business/levelsets`,
          },
        ],
      },
    ],
  },
  {
    href: undefined,
    text: 'Solutions',
    children: [
      {
        heading: 'Skill Development',
        description: 'Build durable capabilities',
        items: [
          {
            ctaText: 'Data & Analytics Academy',
            ctaDescription: 'Enable your employees to drive decisions and innovate',
            ctaTarget: `${config.url.base}business/data-analytics-academy`,
          },
          {
            ctaText: 'Marketing Academy',
            ctaDescription: 'Power business growth with the latest digital marketing skills',
            ctaTarget: `${config.url.base}business/marketing-academy`,
          },
          {
            ctaText: 'Tech Academy',
            ctaDescription: 'Drive your company’s technology transformation',
            ctaTarget: `${config.url.base}business/tech-academy`,
          },
          {
            ctaText: 'Finance Academy',
            ctaDescription: 'Grow a technology-driven finance organization',
            ctaTarget: `${config.url.base}business/finance-academy`,
          },
          {
            ctaText: 'Leadership Academy',
            ctaDescription: 'Empower your workforce to lead and connect',
            ctaTarget: `${config.url.base}business/leadership-academy`,
          },
          {
            ctaText: 'Career Academy',
            ctaDescription: 'Prepare workers for in-demand digital jobs',
            ctaTarget: `${config.url.base}business/career-academy`,
          },
        ],
      },
      {
        heading: 'For Organizations',
        description: 'Solutions for organizations',
        items: [
          {
            ctaText: 'Teams',
            ctaDescription: 'Upskill teams of 5 to 125 employees',
            ctaTarget: `${config.url.base}business/teams`,
          },
          {
            ctaText: 'Universities',
            ctaDescription: 'Coursera for Campus',
            ctaTarget: `${config.url.base}campus`,
          },
          {
            ctaText: 'Governments',
            ctaDescription: 'Coursera for Government',
            ctaTarget: `${config.url.base}government`,
          },
        ],
      },
    ],
  },
  {
    href: undefined,
    text: 'Resources',
    children: [
      {
        heading: 'Insights',
        description: undefined,
        items: [
          {
            ctaText: 'Global Skills Report',
            ctaDescription: 'Get data-driven insights about skills trends for 100+ countries',
            ctaTarget: `${config.url.base}skills-reports/global`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'About Coursera for Business',
            ctaDescription: 'Learn why 3,000 global companies choose Coursera',
            ctaTarget: `${config.url.base}business/ebook/coursera-for-business-data-sheet-2022`,
          },
          {
            ctaText: 'Job Skills of 2023',
            ctaDescription: 'Discover the fastest-growing human and digital skills',
            ctaTarget: `${config.url.base}business/ebook/Job-Skills-2023`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Strategic Leadership',
            ctaDescription: 'Hear how organizations can retain talent and develop leaders',
            ctaTarget: `${config.url.base}business/webcast/strategic-leadership-webinar`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Industry Skills Report',
            ctaDescription: 'See how your industry ranks in critical skills',
            ctaTarget: `${config.url.base}skills-reports`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Business Case for Data Literacy',
            ctaDescription: 'Learn how to improve data literacy and decision making at your company',
            ctaTarget: `${config.url.base}business/ebook/gs-learning-leaders-ebook`,
            shouldOpenInNewWindow: true,
          },
        ],
      },
      {
        heading: 'Resource Hubs',
        description: undefined,
        items: [
          {
            ctaText: 'Coursera Conference',
            ctaDescription: 'Watch the 10th annual gathering about the future of learning and work',
            ctaTarget: `https://conference.coursera.com/home`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Webcasts',
            ctaDescription: 'Listen to engaging conversations with learning and development leaders',
            ctaTarget: `${config.url.base}business/webcast`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Blog',
            ctaDescription: 'Discover how businesses are achieving their goals with skill development programs',
            // eslint-disable-next-line no-restricted-syntax
            ctaTarget: `https://blog.coursera.org/for-business`,
            shouldOpenInNewWindow: true,
          },
        ],
      },
    ],
  },
  {
    href: `${config.url.base}business/teams`,
    text: 'For Teams',
  },
  {
    href: `${config.url.base}business/compare-plans`,
    text: 'Compare Plans',
  },
];

const c4bLinksEnableC4BNavPromo: LinkObject[] = [
  {
    text: 'Why Coursera',
    href: undefined,
    children: [
      {
        heading: 'Why Coursera',
        description: undefined,
        items: [
          {
            ctaText: 'Overview',
            ctaDescription: 'Upskill your workforce with an enterprise-grade skills platform',
            ctaTarget: `${config.url.base}business/why-coursera`,
          },
          {
            ctaText: 'World-Class Content',
            ctaDescription: 'Offer learners content from Yale, Duke, Google and more',
            ctaTarget: `${config.url.base}business/content`,
          },
          {
            ctaText: 'SkillSets',
            ctaDescription: 'Drive transformation with job-based learning for digital roles',
            ctaTarget: `${config.url.base}business/products/skillsets`,
          },
          {
            ctaText: 'Guided Projects',
            ctaDescription: 'Increase skills proficiency and durability with applied learning',
            ctaTarget: `${config.url.base}business/guided-projects`,
          },
          {
            ctaText: 'Skills Dashboard',
            ctaDescription: 'Track, measure, and benchmark skills to maximize training effectiveness',
            ctaTarget: `${config.url.base}business/products/skillsdashboard`,
          },
          {
            ctaText: 'Clips',
            ctaDescription: 'Drive continuous learning with 5-10 minute videos',
            ctaTarget: `${config.url.base}business/clips`,
          },
          {
            ctaText: 'Integrations',
            ctaDescription: 'Connect Coursera to your learning platforms',
            ctaTarget: `${config.url.base}business/integrations`,
          },
          {
            ctaText: 'LevelSets',
            ctaDescription: 'Provide targeted upskilling',
            ctaTarget: `${config.url.base}business/levelsets`,
          },
        ],
      },
    ],
  },
  {
    href: undefined,
    text: 'Solutions',
    children: [
      {
        heading: 'Skill Development',
        description: 'Build durable capabilities',
        items: [
          {
            ctaText: 'Data & Analytics Academy',
            ctaDescription: 'Enable your employees to drive decisions and innovate',
            ctaTarget: `${config.url.base}business/data-analytics-academy`,
          },
          {
            ctaText: 'Marketing Academy',
            ctaDescription: 'Power business growth with the latest digital marketing skills',
            ctaTarget: `${config.url.base}business/marketing-academy`,
          },
          {
            ctaText: 'Tech Academy',
            ctaDescription: 'Drive your company’s technology transformation',
            ctaTarget: `${config.url.base}business/tech-academy`,
          },
          {
            ctaText: 'Finance Academy',
            ctaDescription: 'Grow a technology-driven finance organization',
            ctaTarget: `${config.url.base}business/finance-academy`,
          },
          {
            ctaText: 'Leadership Academy',
            ctaDescription: 'Empower your workforce to lead and connect',
            ctaTarget: `${config.url.base}business/leadership-academy`,
          },
          {
            ctaText: 'Career Academy',
            ctaDescription: 'Prepare workers for in-demand digital jobs',
            ctaTarget: `${config.url.base}business/career-academy`,
          },
        ],
      },
      {
        heading: undefined,
        description: undefined,
        promotion: PROMOTIONS.TEAMS_PROMOTION,
        items: undefined,
      },
    ],
  },
  {
    href: undefined,
    text: 'Resources',
    children: [
      {
        heading: 'Insights',
        description: undefined,
        items: [
          {
            ctaText: 'Global Skills Report',
            ctaDescription: 'Get data-driven insights about skills trends for 100+ countries',
            ctaTarget: `${config.url.base}skills-reports/global`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'About Coursera for Business',
            ctaDescription: 'Learn why 3,000 global companies choose Coursera',
            ctaTarget: `${config.url.base}business/ebook/coursera-for-business-data-sheet-2022`,
          },
          {
            ctaText: 'Job Skills of 2023',
            ctaDescription: 'Discover the fastest-growing human and digital skills',
            ctaTarget: `${config.url.base}business/ebook/Job-Skills-2023`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Strategic Leadership',
            ctaDescription: 'Hear how organizations can retain talent and develop leaders',
            ctaTarget: `${config.url.base}business/webcast/strategic-leadership-webinar`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Industry Skills Report',
            ctaDescription: 'See how your industry ranks in critical skills',
            ctaTarget: `${config.url.base}skills-reports`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Business Case for Data Literacy',
            ctaDescription: 'Learn how to improve data literacy and decision making at your company',
            ctaTarget: `${config.url.base}business/ebook/gs-learning-leaders-ebook`,
            shouldOpenInNewWindow: true,
          },
        ],
      },
      {
        heading: 'Resource Hubs',
        description: undefined,
        items: [
          {
            ctaText: 'Coursera Conference',
            ctaDescription: 'Watch the 10th annual gathering about the future of learning and work',
            ctaTarget: `https://conference.coursera.com/home`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Webcasts',
            ctaDescription: 'Listen to engaging conversations with learning and development leaders',
            ctaTarget: `${config.url.base}business/webcast`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Blog',
            ctaDescription: 'Discover how businesses are achieving their goals with skill development programs',
            // eslint-disable-next-line no-restricted-syntax
            ctaTarget: `https://blog.coursera.org/for-business`,
            shouldOpenInNewWindow: true,
          },
        ],
      },
    ],
  },
  {
    href: `${config.url.base}business/teams`,
    text: 'For Teams',
  },
  {
    href: `${config.url.base}business/compare-plans`,
    text: 'Compare Plans',
  },
];

const c4gLinks: LinkObject[] = [
  {
    text: 'Why Coursera',
    href: undefined,
    children: [
      {
        heading: 'Why Coursera',
        description: undefined,
        items: [
          {
            ctaText: 'Citizen Workforce Development',
            ctaDescription:
              'Equip your citizens with in-demand skills to reduce unemployment and drive economic growth',
            ctaTarget: `${config.url.base}government/citizen-reskilling`,
          },
          {
            ctaText: 'Government Employee Training ',
            ctaDescription: 'Upskill your talent to enable a more efficient and effective public sector',
            ctaTarget: `${config.url.base}government/public-official-upskilling`,
          },
          {
            ctaText: 'SkillSets',
            ctaDescription: 'Drive transformation with job-based learning for digital roles',
            ctaTarget: `${config.url.base}government/skillsets`,
          },
          {
            ctaText: 'World-Class Content',
            ctaDescription: 'Offer learners content from Yale, Duke, Google and more',
            ctaTarget: `${config.url.base}government/content`,
          },
          {
            ctaText: 'Skills Dashboard',
            ctaDescription: 'Track, measure, and benchmark skills to maximize training effectiveness',
            ctaTarget: `${config.url.base}government/skillsdashboard`,
          },
          {
            ctaText: 'Guided Projects',
            ctaDescription: 'Increase skills proficiency and durability with applied learning',
            ctaTarget: `${config.url.base}government/guided-projects`,
          },
          {
            ctaText: 'Clips',
            ctaDescription: 'Drive continuous learning with 5-10 minute videos',
            ctaTarget: `${config.url.base}government/clips`,
          },
        ],
      },
    ],
  },
  {
    href: undefined,
    text: 'Solutions',
    children: [
      {
        heading: 'Skill Development',
        description: 'Build durable capabilities',
        items: [
          {
            ctaText: 'Career Academy',
            ctaDescription: 'Prepare job seekers for in-demand digital jobs',
            ctaTarget: `${config.url.base}government/career-academy`,
          },
          {
            ctaText: 'Data & Analytics Academy',
            ctaDescription: 'Enable your employees to drive decisions and innovate',
            ctaTarget: `${config.url.base}government/data-analytics-academy`,
          },
          {
            ctaText: 'Tech Academy',
            ctaDescription: 'Drive digital transformation ',
            ctaTarget: `${config.url.base}government/tech-academy`,
          },
          {
            ctaText: 'Leadership Academy',
            ctaDescription: 'Empower your workforce to lead without limits',
            ctaTarget: `${config.url.base}government/leadership-academy`,
          },
        ],
      },
      {
        heading: 'For Organizations',
        description: 'Solutions for organizations',
        items: [
          {
            ctaText: 'Companies',
            ctaDescription: 'Coursera for Business',
            ctaTarget: `${config.url.base}business`,
          },
          {
            ctaText: 'Teams',
            ctaDescription: 'Upskill teams of 5 to 125 employees',
            ctaTarget: `${config.url.base}business/teams`,
          },
          {
            ctaText: 'Universities',
            ctaDescription: 'Coursera for Campus',
            ctaTarget: `${config.url.base}campus`,
          },
        ],
      },
    ],
  },
  {
    href: undefined,
    text: 'Resources',
    children: [
      {
        heading: 'Insights',
        description: undefined,
        items: [
          {
            ctaText: 'Women and Online Learning in Emerging Markets',
            ctaDescription: 'Discover insights about women and online learning from the IFC and Coursera.',
            // eslint-disable-next-line no-restricted-syntax
            ctaTarget: `https://blog.coursera.org/ifc-coursera-european-commission-global-gender-study/`,
          },
          {
            ctaText: 'Global Skills Report 2022',
            ctaDescription: 'Get data-driven insights into the world’s top skills trends.',
            ctaTarget: `${config.url.base}skills-reports/global`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'The Great Reskilling',
            ctaDescription:
              'Learn why digital skills training is a transformative and cost-effective way to meet workforce demand.',
            ctaTarget: `${config.url.base}government/great-reskilling-ebook`,
            shouldOpenInNewWindow: true,
          },
        ],
      },
      {
        heading: 'Case Studies',
        description: undefined,
        items: [
          {
            ctaText: 'CINDE Costa Rica',
            ctaDescription:
              'Learn how the government of Costa Rica helps its workforce develop skills to attract new businesses.',
            ctaTarget: `https://pages.coursera-for-business.org/rs/748-MIV-116/images/CINDE_case_study.pdf`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Punjab Skills Development Fund',
            ctaDescription:
              'Learn how the largest skills development fund in Pakistan trains young people for digital careers.',
            ctaTarget: `https://pages.coursera-for-business.org/rs/748-MIV-116/images/PSDF_Case_Study_2021.pdf`,
            shouldOpenInNewWindow: true,
          },
          {
            ctaText: 'Minnesota Department of Employment and Economic Development',
            ctaDescription:
              'See how the state of Minnesota supported unemployed and displaced workers during the pandemic.',
            ctaTarget: `https://pages.coursera-for-business.org/rs/748-MIV-116/images/Minnesota-DEED-case-study.pdf`,
            shouldOpenInNewWindow: true,
          },
        ],
      },
    ],
  },
];

export const NAV_LINKS = {
  [DOMAINS.c4b]: c4bLinks,
  [DOMAINS.c4g]: c4gLinks,
  [DOMAINS.c4c]: c4bLinks,
  [`${DOMAINS.c4b}:enableC4BNavPromo`]: c4bLinksEnableC4BNavPromo,
};
